// ============================================================================
// CONFIG
// ============================================================================

const API_URL = process.env.REACT_APP_API_URL || "https://api.fitadmin.site/api";

// ============================================================================
// TENANT
// ============================================================================

export function getTenantId() {
  const host = window.location.hostname;

  if (host === "localhost" || host === "127.0.0.1") {
    return localStorage.getItem("dev_tenant_id") || process.env.REACT_APP_TENANT_ID || null;
  }

  const parts = host.split(".");
  if (parts.length >= 3) {
    return parts[0];
  }

  console.error("❌ No se pudo determinar tenant desde hostname:", host);
  return null;
}

// ============================================================================
// HEADERS
// ============================================================================

function getHeaders(accessToken = null) {
  const token = accessToken || localStorage.getItem("access");

  const headers = {
    "Content-Type": "application/json",
  };

  const tenantId = getTenantId();
  if (tenantId) {
    headers["X-Tenant-ID"] = tenantId;
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

// ============================================================================
// REFRESH TOKEN
// ============================================================================

async function refreshAccessToken() {
  const refresh = localStorage.getItem("refresh");
  if (!refresh) return null;

  const res = await fetch(`${API_URL}/token/refresh/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh }),
  });

  if (!res.ok) return null;

  const data = await res.json();
  localStorage.setItem("access", data.access);
  return data.access;
}

// ============================================================================
// HANDLE RESPONSE (CON RETRY AUTOMÁTICO)
// ============================================================================

async function handleResponse(response, makeRequest = null) {
  if (response.status === 401 && makeRequest) {
    console.warn("⚠️ Access expirado, intentando refresh...");

    const newAccess = await refreshAccessToken();

    if (newAccess) {
      return makeRequest(newAccess);
    }

    console.warn("❌ Refresh expirado. Cerrando sesión.");
    localStorage.clear();
    window.location.reload();
    return;
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    return {
      error: data.error || data.detail || `Error HTTP ${response.status}`,
      status: response.status,
    };
  }

  return data;
}

// ============================================================================
// HELPER BASE PARA REQUESTS AUTENTICADAS
// ============================================================================

async function authRequest(url, options = {}) {
  const makeRequest = async (accessToken) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        ...getHeaders(accessToken),
        ...(options.headers || {}),
      },
    });

    return handleResponse(res, makeRequest);
  };

  return makeRequest(localStorage.getItem("access"));
}

// ============================================================================
// AUTENTICACIÓN
// ============================================================================

export async function register(data) {
  const res = await fetch(`${API_URL}/register/`, {
    method: "POST",
    headers: getHeaders(null),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function login(username, password) {
  const res = await fetch(`${API_URL}/login/`, {
    method: "POST",
    headers: getHeaders(null),
    body: JSON.stringify({ username, password }),
  });
  return handleResponse(res);
}

export async function logout() {
  const refresh = localStorage.getItem("refresh");

  const res = await fetch(`${API_URL}/logout/`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ refresh }),
  });

  localStorage.clear();
  return handleResponse(res);
}

// ============================================================================
// CLIENTE
// ============================================================================

export const getPanel = () =>
  authRequest(`${API_URL}/panel/`);

export const reservar = (claseId) =>
  authRequest(`${API_URL}/reservar/${claseId}/`, {
    method: "POST",
  });

export const cancelar = (claseId) =>
  authRequest(`${API_URL}/cancelar/${claseId}/`, {
    method: "POST",
  });

export const getMisClases = () =>
  authRequest(`${API_URL}/mis-clases/`);

export const getHistorial = () =>
  authRequest(`${API_URL}/historial/`);

export const getPerfil = () =>
  authRequest(`${API_URL}/perfil/`);

// ============================================================================
// ADMIN
// ============================================================================

// Estadísticas
export const getAdminStats = () =>
  authRequest(`${API_URL}/admin/estadisticas/`);

// Usuarios
export const listarUsuarios = () =>
  authRequest(`${API_URL}/admin/usuarios/`);

export const crearUsuario = (data) =>
  authRequest(`${API_URL}/admin/usuarios/crear/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const editarUsuario = (userId, data) =>
  authRequest(`${API_URL}/admin/usuarios/${userId}/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const eliminarUsuario = (userId) =>
  authRequest(`${API_URL}/admin/usuarios/${userId}/eliminar/`, {
    method: "DELETE",
  });

// Planes
export const listarPlanes = () =>
  authRequest(`${API_URL}/admin/planes/`);

export const crearPlan = (data) =>
  authRequest(`${API_URL}/admin/planes/crear/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const editarPlan = (planId, data) =>
  authRequest(`${API_URL}/admin/planes/${planId}/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const eliminarPlan = (planId) =>
  authRequest(`${API_URL}/admin/planes/${planId}/eliminar/`, {
    method: "DELETE",
  });

// Suscripciones
export const listarSuscripciones = (activas = null, pagoPendiente = false) => {
  const params = new URLSearchParams();
  if (activas !== null && activas !== "") params.append("activas", activas);
  if (pagoPendiente) params.append("pago_pendiente", "true");
  const qs = params.toString();
  return authRequest(`${API_URL}/admin/suscripciones/${qs ? "?" + qs : ""}`);
};

export const crearSuscripcion = (data) =>
  authRequest(`${API_URL}/admin/suscripciones/crear/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const modificarCreditos = (suscripcionId, creditos) =>
  authRequest(`${API_URL}/admin/suscripciones/${suscripcionId}/creditos/`, {
    method: "PUT",
    body: JSON.stringify({ creditos_restantes: creditos }),
  });

export const desactivarSuscripcion = (suscripcionId) =>
  authRequest(`${API_URL}/admin/suscripciones/${suscripcionId}/desactivar/`, {
    method: "POST",
  });

export const activarSuscripcion = (suscripcionId) =>
  authRequest(`${API_URL}/admin/suscripciones/${suscripcionId}/activar/`, {
    method: "POST",
  });

export const eliminarSuscripcion = (suscripcionId) =>
  authRequest(`${API_URL}/admin/suscripciones/${suscripcionId}/eliminar/`, {
    method: "DELETE",
  });

export const editarSuscripcion = (suscripcionId, data) =>
  authRequest(`${API_URL}/admin/suscripciones/${suscripcionId}/editar/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

// Clases
export const listarClasesAdmin = async (futuras = true) => {
  const res = await authRequest(
    `${API_URL}/admin/clases/?futuras=${futuras}`
  );

  if (res?.error) {
    return { success: false, error: res.error };
  }

  return { success: true, data: res };
};
export const crearClase = (data) =>
  authRequest(`${API_URL}/admin/clases/crear/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const actualizarNotaClase = (claseId, nota) =>
  authRequest(`${API_URL}/admin/clases/${claseId}/nota/`, {
    method: "PUT",
    body: JSON.stringify({ nota }),
  });

export const eliminarClase = (claseId) =>
  authRequest(`${API_URL}/admin/clases/${claseId}/eliminar/`, {
    method: "DELETE",
  });

export const removerClienteDeClase = (claseId, clienteId) =>
  authRequest(`${API_URL}/admin/clases/${claseId}/remover/${clienteId}/`, {
    method: "POST",
  });

export const reservarClienteClase = (claseId, clienteId) =>
  authRequest(`${API_URL}/admin/clases/${claseId}/reservar_clase_cliente/`, {
    method: "POST",
    body: JSON.stringify({
      cliente_id: clienteId,
    }),
  });

  // ============================================================================
// PLANTILLAS
// ============================================================================

export const listarPlantillasAdmin = () =>
  authRequest(`${API_URL}/admin/plantillas/`);

export const crearPlantillaAdmin = (data) =>
  authRequest(`${API_URL}/admin/plantillas/crear/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const editarPlantillaAdmin = (plantillaId, data) =>
  authRequest(`${API_URL}/admin/plantillas/${plantillaId}/editar/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const eliminarPlantillaAdmin = (plantillaId) =>
  authRequest(`${API_URL}/admin/plantillas/${plantillaId}/eliminar/`, {
    method: "DELETE",
  });

export const getHorariosPlantillaAdmin = (plantillaId) =>
  authRequest(`${API_URL}/admin/plantillas/${plantillaId}/horarios/`);

export const guardarHorariosPlantillaAdmin = (plantillaId, horarios) =>
  authRequest(`${API_URL}/admin/plantillas/${plantillaId}/horarios/`, {
    method: "POST",
    body: JSON.stringify({ horarios }),
  });

export const generarClasesDesdePlantillasAdmin = ({
  semanas = 4,
  limpiar = false,
}) =>
  authRequest(`${API_URL}/admin/clases/generar/`, {
    method: "POST",
    body: JSON.stringify({ semanas, limpiar }),
  });

// ============================================================================
// SUSCRIPCIONES EXTRA
// ============================================================================

export const renovarSuscripcion = (suscripcionId, data = {}) =>
  authRequest(`${API_URL}/admin/suscripciones/${suscripcionId}/renovar/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const marcarPagado = (suscripcionId) =>
  authRequest(`${API_URL}/admin/suscripciones/${suscripcionId}/marcar-pagado/`, {
    method: "POST",
  });

// ============================================================================
// TENANT CONFIG
// ============================================================================

export const getTenantConfig = () =>
  authRequest(`${API_URL}/tenant/config/`);

export const getNotificacionesConfig = () =>
  authRequest(`${API_URL}/admin/notificaciones/config/`);

export const updateNotificacionesConfig = (data) =>
  authRequest(`${API_URL}/admin/notificaciones/config/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const testEmailNotificaciones = () =>
  authRequest(`${API_URL}/admin/notificaciones/test-email/`, { method: "POST" });

export const getNotificacionesLog = () =>
  authRequest(`${API_URL}/admin/notificaciones/log/`);

// ============================================================================
// CONTABILIDAD
// ============================================================================

export const getContabilidadResumen = (anio, mes) => {
  const params = new URLSearchParams();
  if (anio) params.append("anio", anio);
  if (mes) params.append("mes", mes);
  const qs = params.toString();
  return authRequest(`${API_URL}/admin/contabilidad/resumen/${qs ? "?" + qs : ""}`);
};

export const listarIngresos = (anio, mes) => {
  const params = new URLSearchParams();
  if (anio) params.append("anio", anio);
  if (mes) params.append("mes", mes);
  const qs = params.toString();
  return authRequest(`${API_URL}/admin/contabilidad/ingresos/${qs ? "?" + qs : ""}`);
};

export const crearIngreso = (data) =>
  authRequest(`${API_URL}/admin/contabilidad/ingresos/crear/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const eliminarIngreso = (ingresoId) =>
  authRequest(`${API_URL}/admin/contabilidad/ingresos/${ingresoId}/eliminar/`, {
    method: "DELETE",
  });

export const listarEgresos = (anio, mes) => {
  const params = new URLSearchParams();
  if (anio) params.append("anio", anio);
  if (mes) params.append("mes", mes);
  const qs = params.toString();
  return authRequest(`${API_URL}/admin/contabilidad/egresos/${qs ? "?" + qs : ""}`);
};

export const crearEgreso = (data) =>
  authRequest(`${API_URL}/admin/contabilidad/egresos/crear/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const editarEgreso = (egresoId, data) =>
  authRequest(`${API_URL}/admin/contabilidad/egresos/${egresoId}/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const eliminarEgreso = (egresoId) =>
  authRequest(`${API_URL}/admin/contabilidad/egresos/${egresoId}/eliminar/`, {
    method: "DELETE",
  });

export const verificarDni = (dni) => {
  const tenantId = getTenantId();
  return fetch(`${API_URL}/verificar/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(tenantId ? { "X-Tenant-ID": tenantId } : {}),
    },
    body: JSON.stringify({ dni }),
  }).then((r) => r.json().then((data) => ({ ok: r.ok, status: r.status, data })));
};
